import { useQueries, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { graphqlClient } from '@/core/api/graphql-client'
import {
  setJourneyAnalyticsPlanType,
  trackPaginationFailed,
  trackPaginationTriggered,
} from '@/features/journey/analytics'
import {
  type MilestoneIconSet,
  milestoneIconSetForPlan,
} from '@/features/journey/lib/milestone-icon'
import type { Journey, JourneyGoal } from '@/features/journey/types'
import { JourneyUserDocument, MyGoalDocument } from '@/generated/graphql'

// Goal ids paginated above the core window, kept per active goal so a remount
// (returning from a day route) restores the full window — without this, the
// restored scroll position would land in the wrong place.
const extraPrevCache = new Map<string, string[]>()

export function useActivePlan({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['journey', 'active-plan'],
    queryFn: async () => {
      const data = await graphqlClient.request(JourneyUserDocument)
      const plan = data.me.availablePlans.find(
        (p) => p.id === data.me.activePlanId,
      )
      setJourneyAnalyticsPlanType(plan?.code ?? null)
      if (!plan) return null
      return {
        planId: plan.id,
        planCode: plan.code,
        planTitle: plan.title,
        dayFormat: plan.dayFormat,
        goalId: plan.currentGoalId ?? null,
      }
    },
    // Refetch on re-entry (e.g. returning from a completed day) so progress is fresh.
    staleTime: 0,
    enabled,
  })
}

// Milestone tile set for the active plan (reads the cached active-plan query).
export function useMilestoneIconSet(): MilestoneIconSet {
  const { data } = useActivePlan()
  return milestoneIconSetForPlan(data?.planCode)
}

export function goalQueryOptions(goalId: string | null) {
  return {
    queryKey: ['journey', 'goal', goalId] as const,
    queryFn: async (): Promise<JourneyGoal> => {
      if (!goalId) throw new Error('goalId is required')
      const data = await graphqlClient.request(MyGoalDocument, { goalId })
      if (!data.myGoal) throw new Error(`Goal ${goalId} not found`)
      return data.myGoal
    },
    enabled: !!goalId,
    staleTime: 0,
  }
}

// Active goal only (no prev/next window) — used by journey onboarding.
export function useActiveGoal({ enabled = true } = {}): {
  goal: JourneyGoal | null
  planId: string | null
  isLoading: boolean
  isError: boolean
} {
  const { data: activePlan, isLoading: idLoading } = useActivePlan({ enabled })
  const goalId = activePlan?.goalId ?? null
  const goal = useQuery({
    ...goalQueryOptions(goalId),
    enabled: enabled && !!goalId,
  })
  return {
    goal: goal.data ?? null,
    planId: activePlan?.planId ?? null,
    isLoading: idLoading || (goalId != null && goal.isPending),
    isError: goal.isError || (!idLoading && goalId == null),
  }
}

export function useJourney(): {
  journey: Journey | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  loadPrev: () => void
  loadNext: () => void
  isLoadingPrev: boolean
} {
  const { data: activePlan, isLoading: idLoading } = useActivePlan()
  const activeGoalId = activePlan?.goalId ?? null
  const active = useQuery(goalQueryOptions(activeGoalId))

  const prevId = active.data?.userProgress?.prevGoalId ?? null
  const nextId = active.data?.userProgress?.nextGoalId ?? null

  // Goals loaded above the core window, oldest first (pagination toward goal 1).
  const [extraPrev, setExtraPrev] = useState<string[]>(() =>
    activeGoalId ? (extraPrevCache.get(activeGoalId) ?? []) : [],
  )

  // Re-sync the window when the active goal changes (plan switch, goal advance).
  useEffect(() => {
    setExtraPrev(activeGoalId ? (extraPrevCache.get(activeGoalId) ?? []) : [])
  }, [activeGoalId])

  const [prev, next] = useQueries({
    queries: [goalQueryOptions(prevId), goalQueryOptions(nextId)],
  })
  const extraResults = useQueries({
    queries: extraPrev.map((id) => goalQueryOptions(id)),
  })

  // Topmost loaded goal — its prevGoalId is the next page upward.
  const topmost = extraResults[0]?.data ?? prev.data
  const isLoadingPrev =
    extraPrev.length > 0 && extraResults[0]?.isPending === true

  const loadPrev = useCallback(() => {
    if (!topmost) return // topmost still loading (or nothing to read from)
    trackPaginationTriggered('prev', topmost.number)
    const newPrevId = topmost.userProgress?.prevGoalId
    if (!newPrevId) return
    setExtraPrev((ids) => {
      if (ids.includes(newPrevId)) return ids
      const grown = [newPrevId, ...ids]
      if (activeGoalId) extraPrevCache.set(activeGoalId, grown)
      return grown
    })
  }, [topmost, activeGoalId])

  // Android parity: the initial window already ends at the first goal beyond
  // the active one (locked territory), so next pagination never appends —
  // only the analytics event fires.
  const loadNext = useCallback(() => {
    const last = next.data ?? active.data
    if (!last) return
    trackPaginationTriggered('next', last.number)
  }, [next.data, active.data])

  // Silent pagination failure: analytics only, no retry UI.
  const failedTracked = useRef(new Set<string>())
  useEffect(() => {
    extraResults.forEach((result, i) => {
      const id = extraPrev[i]
      if (!id || !result.isError || failedTracked.current.has(id)) return
      failedTracked.current.add(id)
      trackPaginationFailed(
        'prev',
        result.error instanceof Error ? result.error.name : 'unknown',
      )
    })
  }, [extraResults, extraPrev])

  // Expose the journey only once the full prev/active/next window has
  // resolved. Otherwise a late-arriving previous goal prepends above the
  // active goal and shifts the layout after scroll-to-today has run.
  const prevReady = prevId == null || prev.data != null || prev.isError
  const nextReady = nextId == null || next.data != null || next.isError
  const windowReady = active.data != null && prevReady && nextReady

  const journey: Journey | null =
    windowReady && active.data
      ? {
          goals: [
            ...extraResults.map((r) => r.data),
            prev.data,
            active.data,
            next.data,
          ].filter((g): g is JourneyGoal => !!g),
          currentGoalId: active.data.id,
          currentDayId: active.data.userProgress?.currentDayId ?? null,
          planId: activePlan?.planId ?? null,
        }
      : null

  return {
    journey,
    isLoading:
      idLoading || (activeGoalId != null && !windowReady && !active.isError),
    isError: active.isError,
    error: active.error,
    loadPrev,
    loadNext,
    isLoadingPrev,
  }
}
